/**
 * Projects page — hierarchical project/task management.
 *
 * Top half: card grid of the user's Projects, each with quick aggregates
 * (task count + overall progress). Picking a project loads its full task
 * tree via ``GET /api/projects/{id}/tree`` and renders it with the
 * reusable ``TaskTree`` component. All mutating actions (add, edit,
 * delete, move, link habit/timer) round-trip through the API and then
 * refresh the tree so the UI always reflects server-side roll-up state.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import TaskTree, { TaskTreeNodeData, TaskStatus } from '../components/TaskTree';
import { getApiErrorMessage } from '../utils/apiError';
import PageShell from '../components/PageShell';
import ViewModeToggle from '../components/ViewModeToggle';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import usePersistentViewMode from '../hooks/usePersistentViewMode';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type ProjectStatus =
  | 'planning'
  | 'active'
  | 'paused'
  | 'completed'
  | 'archived';

interface Project {
  id: number;
  user_id: number;
  title: string;
  description?: string | null;
  status: ProjectStatus;
  color: string;
  start_date?: string | null;
  due_date?: string | null;
  created_at: string;
  updated_at?: string | null;
  task_count?: number | null;
  root_task_count?: number | null;
  overall_progress?: number | null;
  /** Fallback hour class for this project's tasks (migration 0033). */
  default_hour_class_id?: number | null;
}

interface ProjectTree extends Project {
  tasks: TaskTreeNodeData[];
}

interface HabitSummary {
  id: number;
  title: string;
  is_active: boolean;
}

interface TimerSummary {
  id: number;
  title: string;
  is_active: boolean;
}

/** Minimal slice of GET /api/hour-classes used by the two selects here. */
interface HourClassSummary {
  id: number;
  name: string;
}

const projectStatusColor: Record<
  ProjectStatus,
  'default' | 'primary' | 'success' | 'warning' | 'error'
> = {
  planning: 'default',
  active: 'primary',
  paused: 'warning',
  completed: 'success',
  archived: 'default',
};

const TASK_STATUSES: TaskStatus[] = [
  'todo',
  'in_progress',
  'blocked',
  'done',
  'cancelled',
];

const PROJECT_STATUSES: ProjectStatus[] = [
  'planning',
  'active',
  'paused',
  'completed',
  'archived',
];

// Mirrors backend MAX_TASK_DEPTH in tasks.py.
const MAX_TASK_DEPTH = 5;

// Planning bounds mirrored from backend schemas.py (migration 0033). The
// server is the authority; these exist so a bad value is refused before it
// costs a round trip.
const MIN_CHUNK_MINUTES = 15;
const MAX_CHUNK_MINUTES = 720;
const MAX_ESTIMATE_MINUTES = 525600;
const TASK_RATINGS = [1, 2, 3, 4, 5];

interface ProjectFormState {
  title: string;
  description: string;
  status: ProjectStatus;
  color: string;
  start_date: string;
  due_date: string;
  default_hour_class_id: number | '';
}

const emptyProjectForm: ProjectFormState = {
  title: '',
  description: '',
  status: 'planning',
  color: '#3B82F6',
  start_date: '',
  due_date: '',
  default_hour_class_id: '',
};

interface TaskFormState {
  title: string;
  description: string;
  status: TaskStatus;
  progress: number;
  is_auto_progress: boolean;
  due_date: string;
  /** Hours as typed ("3.5"); converted to whole minutes on the wire. */
  estimated_hours: string;
  priority: number | '';
  impact: number | '';
  chunk_min_minutes: string;
  chunk_max_minutes: string;
  required_hour_class_id: number | '';
  habit_id: number | '';
  life_timer_id: number | '';
  work_timer_id: number | '';
}

const emptyTaskForm: TaskFormState = {
  title: '',
  description: '',
  status: 'todo',
  progress: 0,
  is_auto_progress: true,
  due_date: '',
  estimated_hours: '',
  // Deliberately not 3: an unrated task is unrated, and inventing a middle
  // rating would let the planner treat a guess as a judgement.
  priority: '',
  impact: '',
  chunk_min_minutes: '',
  chunk_max_minutes: '',
  required_hour_class_id: '',
  habit_id: '',
  life_timer_id: '',
  work_timer_id: '',
};

const toIsoOrNull = (value: string): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const toInputDateTimeLocal = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16);
};

/**
 * Hours as typed → whole minutes for the wire.
 *
 * Blank stays blank the whole way: an unestimated task must not arrive at the
 * server estimated at zero, so this returns null rather than 0 for anything
 * that is not a positive number.
 */
const hoursInputToMinutes = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const hours = Number(trimmed);
  if (!Number.isFinite(hours) || hours <= 0) return null;
  return Math.round(hours * 60);
};

/** Stored minutes → the hours the editor shows (600 → "10", 210 → "3.5"). */
const minutesToHoursInput = (minutes?: number | null): string => {
  if (minutes == null || !Number.isFinite(minutes)) return '';
  // Two decimals resolves to 36 seconds, finer than anything we store, and
  // keeps whole hours reading as "10" rather than "10.00".
  return String(Number((minutes / 60).toFixed(2)));
};

/** A minutes field as typed → an integer, or null when blank/garbled. */
const minutesInputToNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const minutes = Number(trimmed);
  if (!Number.isFinite(minutes)) return null;
  return Math.round(minutes);
};

const numberToInput = (value?: number | null): string =>
  value == null ? '' : String(value);

const formatDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const Projects: React.FC = () => {
  const {
    cardContentPaddingSx,
    dialogContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    panelPaddingSx,
    sectionGapSx,
  } = useLayoutTokens();
  const [viewMode, setViewMode, viewModeControls] = usePersistentViewMode('projects');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedProject, setSelectedProject] = useState<ProjectTree | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectEditing, setProjectEditing] = useState<Project | null>(null);
  const [projectForm, setProjectForm] = useState<ProjectFormState>(emptyProjectForm);
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectDeleteTarget, setProjectDeleteTarget] = useState<Project | null>(null);
  const [projectDeleting, setProjectDeleting] = useState(false);

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogMode, setTaskDialogMode] = useState<'create' | 'edit'>('create');
  const [taskEditing, setTaskEditing] = useState<TaskTreeNodeData | null>(null);
  const [taskParent, setTaskParent] = useState<TaskTreeNodeData | null>(null);
  const [taskForm, setTaskForm] = useState<TaskFormState>(emptyTaskForm);
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskDeleteTarget, setTaskDeleteTarget] = useState<TaskTreeNodeData | null>(null);
  const [taskDeleting, setTaskDeleting] = useState(false);

  const [moveDialogTask, setMoveDialogTask] = useState<TaskTreeNodeData | null>(null);
  const [moveParentChoice, setMoveParentChoice] = useState<number | 'root'>('root');
  const [moveSaving, setMoveSaving] = useState(false);

  const [habits, setHabits] = useState<HabitSummary[]>([]);
  const [timers, setTimers] = useState<TimerSummary[]>([]);
  const [hourClasses, setHourClasses] = useState<HourClassSummary[]>([]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get<Project[]>(`${API_BASE_URL}/api/projects/`);
      setProjects(response.data);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load projects'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjectTree = useCallback(async (projectId: number) => {
    try {
      setSelectedLoading(true);
      const response = await axios.get<ProjectTree>(
        `${API_BASE_URL}/api/projects/${projectId}/tree`,
      );
      setSelectedProject(response.data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load project tasks'));
    } finally {
      setSelectedLoading(false);
    }
  }, []);

  const fetchTrackers = useCallback(async () => {
    // Lazy-loaded when the user first opens a task dialog so we don't slow
    // down the initial project list fetch.
    try {
      const [habitResp, timerResp] = await Promise.all([
        axios.get<HabitSummary[]>(`${API_BASE_URL}/api/habits/`),
        axios.get<TimerSummary[]>(`${API_BASE_URL}/api/timers/?active_only=false`),
      ]);
      setHabits(habitResp.data);
      setTimers(timerResp.data);
    } catch (err: any) {
      // Non-fatal; the linking dropdowns will just be empty.
      // eslint-disable-next-line no-console
      console.warn('Failed to load habits/timers for linking', err);
    }
  }, []);

  const fetchHourClasses = useCallback(async () => {
    // Fetched once per page mount: the two selects that consume it are inside
    // dialogs, and re-reading the list per keystroke would be pure noise.
    try {
      const response = await axios.get<HourClassSummary[]>(
        `${API_BASE_URL}/api/hour-classes`,
      );
      setHourClasses(Array.isArray(response.data) ? response.data : []);
    } catch {
      // Non-fatal: the selects degrade to their "no constraint" option.
      setHourClasses([]);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchHourClasses();
  }, [fetchHourClasses]);

  const hourClassName = (id?: number | null): string | null => {
    if (id == null) return null;
    return hourClasses.find((entry) => entry.id === id)?.name ?? `#${id}`;
  };

  // ---------------- Project CRUD ----------------

  const openCreateProject = () => {
    setProjectEditing(null);
    setProjectForm(emptyProjectForm);
    setProjectDialogOpen(true);
  };

  const openEditProject = (project: Project) => {
    setProjectEditing(project);
    setProjectForm({
      title: project.title,
      description: project.description || '',
      status: project.status,
      color: project.color || '#3B82F6',
      start_date: toInputDateTimeLocal(project.start_date),
      due_date: toInputDateTimeLocal(project.due_date),
      default_hour_class_id: project.default_hour_class_id ?? '',
    });
    setProjectDialogOpen(true);
  };

  const saveProject = async () => {
    const title = projectForm.title.trim();
    if (!title) {
      setError('Project title is required');
      return;
    }
    try {
      setProjectSaving(true);
      const payload = {
        title,
        description: projectForm.description.trim() || null,
        status: projectForm.status,
        color: projectForm.color,
        start_date: toIsoOrNull(projectForm.start_date),
        due_date: toIsoOrNull(projectForm.due_date),
        default_hour_class_id:
          projectForm.default_hour_class_id === ''
            ? null
            : Number(projectForm.default_hour_class_id),
      };
      if (projectEditing) {
        await axios.put(`${API_BASE_URL}/api/projects/${projectEditing.id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/api/projects/`, payload);
      }
      setProjectDialogOpen(false);
      await fetchProjects();
      if (selectedProject && projectEditing && projectEditing.id === selectedProject.id) {
        await fetchProjectTree(selectedProject.id);
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save project'));
    } finally {
      setProjectSaving(false);
    }
  };

  const deleteProject = async () => {
    if (!projectDeleteTarget) return;
    try {
      setProjectDeleting(true);
      await axios.delete(`${API_BASE_URL}/api/projects/${projectDeleteTarget.id}`);
      if (selectedProject && selectedProject.id === projectDeleteTarget.id) {
        setSelectedProject(null);
      }
      setProjectDeleteTarget(null);
      await fetchProjects();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete project'));
    } finally {
      setProjectDeleting(false);
    }
  };

  // ---------------- Task planning fields ----------------

  // The server validates all of this too and stays the authority; mirroring it
  // here means a bad estimate or an inverted chunk pair is caught while the
  // user is still looking at the field rather than after a 400.
  const estimatedMinutes = hoursInputToMinutes(taskForm.estimated_hours);
  const chunkMin = minutesInputToNumber(taskForm.chunk_min_minutes);
  const chunkMax = minutesInputToNumber(taskForm.chunk_max_minutes);

  const estimateError = ((): string | null => {
    if (!taskForm.estimated_hours.trim()) return null;
    if (estimatedMinutes === null) return 'Enter a positive number of hours.';
    if (estimatedMinutes > MAX_ESTIMATE_MINUTES) {
      return 'That is over a year of work — break the task down.';
    }
    return null;
  })();

  const chunkRangeError = (value: number | null): string | null =>
    value != null && (value < MIN_CHUNK_MINUTES || value > MAX_CHUNK_MINUTES)
      ? `Chunks run ${MIN_CHUNK_MINUTES}–${MAX_CHUNK_MINUTES} minutes.`
      : null;

  const chunkMinError = chunkRangeError(chunkMin);
  const chunkMaxError =
    chunkRangeError(chunkMax) ||
    (chunkMin != null && chunkMax != null && chunkMax < chunkMin
      ? 'Maximum chunk cannot be below the minimum.'
      : null);

  const taskFormInvalid = Boolean(estimateError || chunkMinError || chunkMaxError);

  // ---------------- Task CRUD ----------------

  const openCreateTask = async (parent: TaskTreeNodeData | null) => {
    if (!selectedProject) return;
    if (habits.length === 0 && timers.length === 0) {
      // Best-effort: load trackers the first time the user opens the editor.
      fetchTrackers();
    }
    setTaskDialogMode('create');
    setTaskEditing(null);
    setTaskParent(parent);
    setTaskForm(emptyTaskForm);
    setTaskDialogOpen(true);
  };

  const openEditTask = async (task: TaskTreeNodeData) => {
    if (habits.length === 0 && timers.length === 0) {
      fetchTrackers();
    }
    setTaskDialogMode('edit');
    setTaskEditing(task);
    setTaskParent(null);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      status: task.status,
      progress: task.progress || 0,
      is_auto_progress: task.is_auto_progress,
      due_date: toInputDateTimeLocal(task.due_date),
      estimated_hours: minutesToHoursInput(task.estimated_minutes),
      priority: task.priority ?? '',
      impact: task.impact ?? '',
      chunk_min_minutes: numberToInput(task.chunk_min_minutes),
      chunk_max_minutes: numberToInput(task.chunk_max_minutes),
      required_hour_class_id: task.required_hour_class_id ?? '',
      habit_id: task.habit_id ?? '',
      life_timer_id: task.life_timer_id ?? '',
      work_timer_id: task.work_timer_id ?? '',
    });
    setTaskDialogOpen(true);
  };

  const openLinkTask = async (task: TaskTreeNodeData) => {
    // "Link" just opens the normal edit dialog — the habit/timer selectors
    // live there. Keeping a dedicated button keeps the affordance obvious.
    await openEditTask(task);
  };

  const saveTask = async () => {
    if (!selectedProject) return;
    const title = taskForm.title.trim();
    if (!title) {
      setError('Task title is required');
      return;
    }
    if (taskFormInvalid) {
      setError(estimateError || chunkMinError || chunkMaxError);
      return;
    }
    try {
      setTaskSaving(true);
      const basePayload = {
        title,
        description: taskForm.description.trim() || null,
        status: taskForm.status,
        progress: taskForm.progress,
        is_auto_progress: taskForm.is_auto_progress,
        due_date: toIsoOrNull(taskForm.due_date),
        // Hours in the UI, minutes on the wire; blank means unestimated, and
        // an unestimated task sends null rather than a fabricated 0.
        estimated_minutes: estimatedMinutes,
        priority: taskForm.priority === '' ? null : Number(taskForm.priority),
        impact: taskForm.impact === '' ? null : Number(taskForm.impact),
        chunk_min_minutes: chunkMin,
        chunk_max_minutes: chunkMax,
        required_hour_class_id:
          taskForm.required_hour_class_id === ''
            ? null
            : Number(taskForm.required_hour_class_id),
        habit_id: taskForm.habit_id === '' ? null : Number(taskForm.habit_id),
        life_timer_id: taskForm.life_timer_id === '' ? null : Number(taskForm.life_timer_id),
        work_timer_id: taskForm.work_timer_id === '' ? null : Number(taskForm.work_timer_id),
      };
      if (taskDialogMode === 'create') {
        await axios.post(
          `${API_BASE_URL}/api/projects/${selectedProject.id}/tasks`,
          {
            ...basePayload,
            parent_task_id: taskParent ? taskParent.id : null,
            position: 0,
          },
        );
      } else if (taskEditing) {
        await axios.put(
          `${API_BASE_URL}/api/tasks/${taskEditing.id}`,
          basePayload,
        );
      }
      setTaskDialogOpen(false);
      await fetchProjectTree(selectedProject.id);
      await fetchProjects();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save task'));
    } finally {
      setTaskSaving(false);
    }
  };

  const deleteTask = async () => {
    if (!taskDeleteTarget || !selectedProject) return;
    try {
      setTaskDeleting(true);
      await axios.delete(`${API_BASE_URL}/api/tasks/${taskDeleteTarget.id}`);
      setTaskDeleteTarget(null);
      await fetchProjectTree(selectedProject.id);
      await fetchProjects();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete task'));
    } finally {
      setTaskDeleting(false);
    }
  };

  const toggleStatus = async (task: TaskTreeNodeData) => {
    if (!selectedProject) return;
    const nextStatus: TaskStatus = task.status === 'done' ? 'todo' : 'done';
    try {
      await axios.put(`${API_BASE_URL}/api/tasks/${task.id}`, {
        status: nextStatus,
      });
      await fetchProjectTree(selectedProject.id);
      await fetchProjects();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update task status'));
    }
  };

  // ---------------- Reparenting ----------------

  /** Flatten the project's task tree into a list of valid move targets. */
  const moveTargets = useMemo(() => {
    if (!selectedProject || !moveDialogTask) return [];
    const forbidden = new Set<number>([moveDialogTask.id]);

    const collectForbidden = (n: TaskTreeNodeData) => {
      n.children.forEach((c) => {
        forbidden.add(c.id);
        collectForbidden(c);
      });
    };
    collectForbidden(moveDialogTask);

    const results: { id: number; title: string; depth: number }[] = [];
    const walk = (nodes: TaskTreeNodeData[], depth: number) => {
      nodes.forEach((n) => {
        if (!forbidden.has(n.id)) {
          results.push({ id: n.id, title: n.title, depth });
        }
        walk(n.children, depth + 1);
      });
    };
    walk(selectedProject.tasks, 0);
    return results;
  }, [selectedProject, moveDialogTask]);

  const openMoveDialog = (task: TaskTreeNodeData) => {
    setMoveDialogTask(task);
    setMoveParentChoice(task.parent_task_id ?? 'root');
  };

  const saveMove = async () => {
    if (!moveDialogTask || !selectedProject) return;
    try {
      setMoveSaving(true);
      await axios.post(`${API_BASE_URL}/api/tasks/${moveDialogTask.id}/move`, {
        new_parent_task_id: moveParentChoice === 'root' ? null : moveParentChoice,
      });
      setMoveDialogTask(null);
      await fetchProjectTree(selectedProject.id);
      await fetchProjects();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to move task'));
    } finally {
      setMoveSaving(false);
    }
  };

  // ---------------- Rendering ----------------

  if (loading) {
    return (
      <PageShell>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (selectedProject) {
    return (
      <PageShell>
        <Box sx={sectionGapSx}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setSelectedProject(null)}
          >
            All Projects
          </Button>
        </Box>

        <Box
          sx={[
            pageHeaderSx,
            {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            },
          ]}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h4" component="h1">
                {selectedProject.title}
              </Typography>
              <Chip
                label={selectedProject.status}
                color={projectStatusColor[selectedProject.status]}
                size="small"
              />
            </Stack>
            {selectedProject.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedProject.description}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip label={`${selectedProject.task_count ?? 0} tasks`} size="small" />
              <Chip
                label={`${selectedProject.overall_progress ?? 0}% overall`}
                color="primary"
                size="small"
              />
              {selectedProject.default_hour_class_id != null && (
                <Chip
                  label={`Default hours: ${hourClassName(
                    selectedProject.default_hour_class_id,
                  )}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, selectedProject.overall_progress ?? 0))}
              sx={{ mt: 1, height: 8, borderRadius: 4, maxWidth: 360 }}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => openEditProject(selectedProject)}>
              Edit Project
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openCreateTask(null)}
            >
              Add Task
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <TaskTree
                nodes={selectedProject.tasks}
                maxDepth={MAX_TASK_DEPTH}
                onAddChild={openCreateTask}
                onEdit={openEditTask}
                onDelete={(t) => setTaskDeleteTarget(t)}
                onMove={openMoveDialog}
                onLink={openLinkTask}
                onToggleStatus={toggleStatus}
              />
            </CardContent>
          </Card>
        )}

        {/* Task create/edit dialog */}
        <Dialog
          open={taskDialogOpen}
          onClose={() => (taskSaving ? undefined : setTaskDialogOpen(false))}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {taskDialogMode === 'create'
              ? taskParent
                ? `Add subtask under "${taskParent.title}"`
                : 'Add Task'
              : 'Edit Task'}
          </DialogTitle>
          <DialogContent sx={dialogContentPaddingSx}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Title"
                fullWidth
                required
                value={taskForm.title}
                onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))}
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={2}
                maxRows={8}
                value={taskForm.description}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, description: e.target.value }))
                }
              />
              <FormControl fullWidth>
                <InputLabel id="task-status-label">Status</InputLabel>
                <Select
                  labelId="task-status-label"
                  label="Status"
                  value={taskForm.status}
                  onChange={(e) =>
                    setTaskForm((f) => ({
                      ...f,
                      status: e.target.value as TaskStatus,
                    }))
                  }
                >
                  {TASK_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Typography variant="body2" gutterBottom>
                  Progress: {taskForm.progress}%
                </Typography>
                <Slider
                  value={taskForm.progress}
                  min={0}
                  max={100}
                  step={5}
                  disabled={taskForm.is_auto_progress && taskDialogMode === 'edit'}
                  onChange={(_, v) =>
                    setTaskForm((f) => ({ ...f, progress: v as number }))
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={taskForm.is_auto_progress}
                      onChange={(e) =>
                        setTaskForm((f) => ({
                          ...f,
                          is_auto_progress: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Auto-roll up progress from subtasks"
                />
              </Box>
              <TextField
                label="Due date"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={taskForm.due_date}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, due_date: e.target.value }))
                }
              />
              <TextField
                label="Estimated hours"
                type="number"
                fullWidth
                value={taskForm.estimated_hours}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, estimated_hours: e.target.value }))
                }
                error={Boolean(estimateError)}
                helperText={
                  estimateError ||
                  'Decimals are fine — 3.5 is 3h 30m. Stored to the nearest minute; blank leaves the task unestimated.'
                }
                inputProps={{ min: 0, step: 0.25 }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="task-priority-label">Priority</InputLabel>
                  <Select
                    labelId="task-priority-label"
                    label="Priority"
                    value={taskForm.priority}
                    onChange={(e) =>
                      setTaskForm((f) => ({
                        ...f,
                        priority: (e.target.value === '' ? '' : Number(e.target.value)) as any,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Unrated</em>
                    </MenuItem>
                    {TASK_RATINGS.map((rating) => (
                      <MenuItem key={rating} value={rating}>
                        {rating}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>1 lowest, 5 highest. Unrated is a real answer.</FormHelperText>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="task-impact-label">Impact</InputLabel>
                  <Select
                    labelId="task-impact-label"
                    label="Impact"
                    value={taskForm.impact}
                    onChange={(e) =>
                      setTaskForm((f) => ({
                        ...f,
                        impact: (e.target.value === '' ? '' : Number(e.target.value)) as any,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Unrated</em>
                    </MenuItem>
                    {TASK_RATINGS.map((rating) => (
                      <MenuItem key={rating} value={rating}>
                        {rating}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>1 lowest, 5 highest. Unrated is a real answer.</FormHelperText>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Minimum chunk (minutes)"
                  type="number"
                  fullWidth
                  value={taskForm.chunk_min_minutes}
                  onChange={(e) =>
                    setTaskForm((f) => ({ ...f, chunk_min_minutes: e.target.value }))
                  }
                  error={Boolean(chunkMinError)}
                  helperText={
                    chunkMinError ||
                    `${MIN_CHUNK_MINUTES}–${MAX_CHUNK_MINUTES} min; blank uses your account default.`
                  }
                  inputProps={{
                    min: MIN_CHUNK_MINUTES,
                    max: MAX_CHUNK_MINUTES,
                    step: 5,
                  }}
                />
                <TextField
                  label="Maximum chunk (minutes)"
                  type="number"
                  fullWidth
                  value={taskForm.chunk_max_minutes}
                  onChange={(e) =>
                    setTaskForm((f) => ({ ...f, chunk_max_minutes: e.target.value }))
                  }
                  error={Boolean(chunkMaxError)}
                  helperText={
                    chunkMaxError ||
                    `${MIN_CHUNK_MINUTES}–${MAX_CHUNK_MINUTES} min; blank uses your account default.`
                  }
                  inputProps={{
                    min: MIN_CHUNK_MINUTES,
                    max: MAX_CHUNK_MINUTES,
                    step: 5,
                  }}
                />
              </Stack>
              <FormControl fullWidth>
                <InputLabel id="task-hour-class-label">Required hour class</InputLabel>
                <Select
                  labelId="task-hour-class-label"
                  label="Required hour class"
                  value={taskForm.required_hour_class_id}
                  onChange={(e) =>
                    setTaskForm((f) => ({
                      ...f,
                      required_hour_class_id: (e.target.value === ''
                        ? ''
                        : Number(e.target.value)) as any,
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>Inherit from project — any time</em>
                  </MenuItem>
                  {hourClasses.map((hourClass) => (
                    <MenuItem key={hourClass.id} value={hourClass.id}>
                      {hourClass.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Blank inherits the project default; with no project default the
                  task can be scheduled at any hour.
                </FormHelperText>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="task-habit-label">Linked habit (optional)</InputLabel>
                <Select
                  labelId="task-habit-label"
                  label="Linked habit (optional)"
                  value={taskForm.habit_id}
                  onChange={(e) =>
                    setTaskForm((f) => ({
                      ...f,
                      habit_id: (e.target.value === '' ? '' : Number(e.target.value)) as any,
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {habits.map((h) => (
                    <MenuItem key={h.id} value={h.id}>
                      {h.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="task-timer-label">Linked life timer (optional)</InputLabel>
                <Select
                  labelId="task-timer-label"
                  label="Linked life timer (optional)"
                  value={taskForm.life_timer_id}
                  onChange={(e) =>
                    setTaskForm((f) => ({
                      ...f,
                      life_timer_id: (e.target.value === '' ? '' : Number(e.target.value)) as any,
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {timers.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTaskDialogOpen(false)} disabled={taskSaving}>
              Cancel
            </Button>
            <Button
              onClick={saveTask}
              variant="contained"
              disabled={taskSaving || taskFormInvalid}
            >
              {taskSaving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Task delete confirm */}
        <Dialog
          open={Boolean(taskDeleteTarget)}
          onClose={() => setTaskDeleteTarget(null)}
        >
          <DialogTitle>Delete Task</DialogTitle>
          <DialogContent sx={dialogContentPaddingSx}>
            <DialogContentText>
              Delete <strong>&quot;{taskDeleteTarget?.title}&quot;</strong> and all its
              subtasks? This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTaskDeleteTarget(null)} disabled={taskDeleting}>
              Cancel
            </Button>
            <Button
              onClick={deleteTask}
              color="error"
              variant="contained"
              disabled={taskDeleting}
            >
              {taskDeleting ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Move dialog */}
        <Dialog
          open={Boolean(moveDialogTask)}
          onClose={() => (moveSaving ? undefined : setMoveDialogTask(null))}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            Move &quot;{moveDialogTask?.title}&quot;
          </DialogTitle>
          <DialogContent sx={dialogContentPaddingSx}>
            <DialogContentText sx={{ mb: 2 }}>
              Pick a new parent. Descendants of this task are excluded to prevent
              cycles. Select "Project root" to promote to a top-level task.
            </DialogContentText>
            <FormControl fullWidth>
              <InputLabel id="move-parent-label">New parent</InputLabel>
              <Select
                labelId="move-parent-label"
                label="New parent"
                value={moveParentChoice}
                onChange={(e) =>
                  setMoveParentChoice(
                    e.target.value === 'root' ? 'root' : Number(e.target.value),
                  )
                }
              >
                <MenuItem value="root">
                  <em>Project root (no parent)</em>
                </MenuItem>
                {moveTargets.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {'— '.repeat(t.depth)}
                    {t.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMoveDialogTask(null)} disabled={moveSaving}>
              Cancel
            </Button>
            <Button onClick={saveMove} variant="contained" disabled={moveSaving}>
              {moveSaving ? <CircularProgress size={20} /> : 'Move'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reuse the same project dialog for editing from the detail view */}
        {renderProjectDialog()}
      </PageShell>
    );
  }

  function renderProjectDialog() {
    return (
      <Dialog
        open={projectDialogOpen}
        onClose={() => (projectSaving ? undefined : setProjectDialogOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{projectEditing ? 'Edit Project' : 'New Project'}</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={projectForm.title}
              onChange={(e) =>
                setProjectForm((f) => ({ ...f, title: e.target.value }))
              }
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              maxRows={8}
              value={projectForm.description}
              onChange={(e) =>
                setProjectForm((f) => ({ ...f, description: e.target.value }))
              }
            />
            <FormControl fullWidth>
              <InputLabel id="project-status-label">Status</InputLabel>
              <Select
                labelId="project-status-label"
                label="Status"
                value={projectForm.status}
                onChange={(e) =>
                  setProjectForm((f) => ({
                    ...f,
                    status: e.target.value as ProjectStatus,
                  }))
                }
              >
                {PROJECT_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Color"
              type="color"
              value={projectForm.color}
              onChange={(e) =>
                setProjectForm((f) => ({ ...f, color: e.target.value }))
              }
              sx={{ width: 120 }}
            />
            <TextField
              label="Start date"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={projectForm.start_date}
              onChange={(e) =>
                setProjectForm((f) => ({ ...f, start_date: e.target.value }))
              }
            />
            <TextField
              label="Due date"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={projectForm.due_date}
              onChange={(e) =>
                setProjectForm((f) => ({ ...f, due_date: e.target.value }))
              }
            />
            <FormControl fullWidth>
              <InputLabel id="project-hour-class-label">Default hour class</InputLabel>
              <Select
                labelId="project-hour-class-label"
                label="Default hour class"
                value={projectForm.default_hour_class_id}
                onChange={(e) =>
                  setProjectForm((f) => ({
                    ...f,
                    default_hour_class_id: (e.target.value === ''
                      ? ''
                      : Number(e.target.value)) as any,
                  }))
                }
              >
                <MenuItem value="">
                  <em>Any time — no default</em>
                </MenuItem>
                {hourClasses.map((hourClass) => (
                  <MenuItem key={hourClass.id} value={hourClass.id}>
                    {hourClass.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Tasks here inherit this hour class unless they name their own.
              </FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialogOpen(false)} disabled={projectSaving}>
            Cancel
          </Button>
          <Button onClick={saveProject} variant="contained" disabled={projectSaving}>
            {projectSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ---------------- Project list view ----------------

  return (
    <PageShell>
      <Box
        sx={[
          pageHeaderSx,
          {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          },
        ]}
      >
        <Box>
          <Typography variant="h3" component="h1">
            Projects
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Plan bodies of work as a hierarchy of tasks. Subtasks roll up their
            progress to the parent; link any task to a habit or timer to track
            activity.
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          alignSelf={{ xs: 'stretch', sm: 'center' }}
          justifyContent="flex-end"
        >
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            ariaLabel="project view mode"
            isOverride={viewModeControls.isOverride}
            onUseDefault={viewModeControls.useDefault}
          />
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={openCreateProject}
          >
            New Project
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {projects.length === 0 ? (
        <Card>
          {/* Deliberately airy empty state — the trailing py outranks the
              token's vertical axis on purpose; horizontal comes from the token. */}
          <CardContent sx={[cardContentPaddingSx, { textAlign: 'center', py: { xs: 4, md: 6 } }]}>
            <Typography variant="h6" gutterBottom>
              No projects yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a project to start breaking work down into nested tasks.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateProject}>
              Create Your First Project
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={pageGridSpacing}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} lg={4} key={project.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea
                  onClick={() => fetchProjectTree(project.id)}
                  sx={{ flexGrow: 1 }}
                >
                  <CardContent sx={cardContentPaddingSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: project.color || '#3B82F6',
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                        {project.title}
                      </Typography>
                    </Box>
                    {project.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 2,
                        }}
                      >
                        {project.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={project.status}
                        color={projectStatusColor[project.status]}
                        size="small"
                      />
                      <Chip
                        label={`${project.task_count ?? 0} tasks`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`${project.overall_progress ?? 0}% complete`}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, project.overall_progress ?? 0))}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </CardContent>
                </CardActionArea>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: 1,
                    pb: 1,
                    gap: 0.5,
                  }}
                >
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditProject(project);
                      }}
                      aria-label={`Edit ${project.title}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectDeleteTarget(project);
                      }}
                      aria-label={`Delete ${project.title}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Stack spacing={1}>
          {projects.map((project) => {
            const startDate = formatDate(project.start_date);
            const dueDate = formatDate(project.due_date);
            const progress = Math.max(0, Math.min(100, project.overall_progress ?? 0));

            return (
              <Paper key={project.id} sx={panelPaddingSx}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'minmax(0, 1.4fr) minmax(180px, 0.8fr) minmax(180px, 0.8fr) auto',
                    },
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                  }}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => fetchProjectTree(project.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        fetchProjectTree(project.id);
                      }
                    }}
                    sx={{ minWidth: 0, cursor: 'pointer' }}
                    aria-label={`Open ${project.title}`}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: project.color || '#3B82F6',
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                        {project.title}
                      </Typography>
                    </Stack>
                    {project.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mt: 0.5,
                        }}
                      >
                        {project.description}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                      <Chip
                        label={project.status}
                        color={projectStatusColor[project.status]}
                        size="small"
                      />
                      <Chip
                        label={`${project.task_count ?? 0} tasks`}
                        size="small"
                        variant="outlined"
                      />
                      {project.root_task_count != null && (
                        <Chip
                          label={`${project.root_task_count} root`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {progress}% complete
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {startDate && <Chip label={`Start ${startDate}`} size="small" variant="outlined" />}
                    {dueDate && <Chip label={`Due ${dueDate}`} size="small" variant="outlined" />}
                    {!startDate && !dueDate && (
                      <Typography variant="caption" color="text.secondary">
                        No dates
                      </Typography>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEditProject(project)}
                        aria-label={`Edit ${project.title}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setProjectDeleteTarget(project)}
                        aria-label={`Delete ${project.title}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Project create/edit dialog */}
      {renderProjectDialog()}

      {/* Project delete confirm */}
      <Dialog
        open={Boolean(projectDeleteTarget)}
        onClose={() => setProjectDeleteTarget(null)}
      >
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText>
            Delete <strong>&quot;{projectDeleteTarget?.title}&quot;</strong> and all its
            tasks? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDeleteTarget(null)} disabled={projectDeleting}>
            Cancel
          </Button>
          <Button
            onClick={deleteProject}
            color="error"
            variant="contained"
            disabled={projectDeleting}
          >
            {projectDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Projects;
